<template>
  <div class="modal cookie-modal" v-if="!cookiesAccepted">
    <div class="overlay" @click="$parent.closeRegister"></div>
    <div class="wrapper">
      <div class="cookie-content">
        <form>
          <!-- Main Categories View -->
          <div v-if="!selectedCategory" class="cookie-view">
            <h2 class="cmp-consentmodal-headline">
              <strong>{{
                $t(`We use tracking to give you the best possible experience`)
              }}</strong>
            </h2>
            <p class="cmp-consentmodal-intro">
              {{
                $t(
                  `Of course, you're in control of your data and can revoke your consent at any time. More details can be found in our`
                )
              }}
              <a @click="$parent.goToPrivacy()" class="link">{{
                $t(`Privacy Policy`)
              }}</a>
              {{ $t(`and the use of Cookies in the`) }}
              <a @click="$parent.goToCookiesPage()" class="link">{{
                $t(`Cookies Policy`)
              }}</a
              >.
            </p>

            <div class="cookie-categories">
              <!-- Essential -->
              <section class="cookie-category">
                <div class="chekbox-container">
                  <label class="chekbox-label">
                    <div class="chekbox-label-wrapper">
                      <input
                        type="checkbox"
                        v-model="essentialEnabled"
                        disabled
                      />
                      <div class="checkbox"></div>
                      <h3 class="desc">{{ $t(`Essential`) }}</h3>
                    </div>
                  </label>
                </div>
                <p class="desc description">
                  {{
                    $t(
                      `Essential cookies are always on. Those technologies enable you to access the basic functions of the platform.`
                    )
                  }}
                </p>
                <button class="details-btn" @click="showCategory('essential')">
                  {{ $t(`More`) }}
                </button>
              </section>

              <!-- Optimization & Performance -->
              <section class="cookie-category">
                <div class="chekbox-container">
                  <label class="chekbox-label">
                    <div class="chekbox-label-wrapper">
                      <input type="checkbox" v-model="performanceEnabled" />
                      <div class="checkbox"></div>
                      <h3 class="desc">
                        {{ $t(`Optimisation & performance`) }}
                      </h3>
                    </div>
                  </label>
                </div>
                <p class="desc description">
                  {{
                    $t(
                      `Performance cookies tell us how you use our platform. This enables us to improve the usability of our content, services, and tools.`
                    )
                  }}
                </p>
                <button
                  class="details-btn"
                  @click="showCategory('performance')"
                >
                  {{ $t(`More`) }}
                </button>
              </section>

              <!-- Personalization -->
              <section class="cookie-category">
                <div class="chekbox-container">
                  <label class="chekbox-label">
                    <div class="chekbox-label-wrapper">
                      <input type="checkbox" v-model="personalizationEnabled" />
                      <div class="checkbox"></div>
                      <h3 class="desc">{{ $t(`Personalisation`) }}</h3>
                    </div>
                  </label>
                </div>
                <p class="desc description">
                  {{
                    $t(
                      `We can provide you with personalised content tailored to your interests.`
                    )
                  }}
                </p>
                <button
                  class="details-btn"
                  @click="showCategory('personalization')"
                >
                  {{ $t(`More`) }}
                </button>
              </section>
            </div>

            <div class="cmp-buttons">
              <button class="btn btn-primary" @click="saveCookiePreferences">
                {{ $t(`Save`) }}
              </button>
              <button class="btn btn-secondary" @click="denyAllCookies">
                {{ $t(`Deny all`) }}
              </button>
              <button class="btn btn-primary" @click="acceptAllCookies">
                {{ $t(`Accept all`) }}
              </button>
            </div>
          </div>

          <!-- Detailed Services View -->
          <div v-else class="cookie-view">
            <h2 class="cmp-consentmodal-headline">
              <strong>{{
                $t(`We use tracking to give you the best possible experience`)
              }}</strong>
            </h2>
            <p
              class="cmp-consentmodal-intro"
              v-if="selectedCategory === 'essential'"
            >
              {{
                $t(
                  `Essential cookies are always on. Those technologies enable you to access the basic functions of the platform.`
                )
              }}
            </p>

            <div class="cmp-consentmodal-services">
              <!-- Essential Services -->
              <template v-if="selectedCategory === 'essential'">
                <section
                  v-for="service in essentialServices"
                  :key="service.id"
                  class="service-item"
                >
                  <div class="chekbox-container">
                    <label class="chekbox-label">
                      <div class="chekbox-label-wrapper">
                        <input
                          type="checkbox"
                          v-model="service.enabled"
                          disabled
                        />
                        <div class="checkbox"></div>
                        <h3 class="desc">{{ service.name }}</h3>
                      </div>
                    </label>
                  </div>
                  <p class="desc description">{{ service.description }}</p>
                </section>
              </template>

              <!-- Performance Services -->
              <template v-if="selectedCategory === 'performance'">
                <section
                  v-for="service in performanceServices"
                  :key="service.id"
                  class="service-item"
                >
                  <div class="chekbox-container">
                    <label class="chekbox-label">
                      <div class="chekbox-label-wrapper">
                        <input type="checkbox" v-model="service.enabled" />
                        <div class="checkbox"></div>
                        <h3 class="desc">{{ service.name }}</h3>
                      </div>
                    </label>
                  </div>
                  <p class="desc description">{{ service.description }}</p>
                </section>
              </template>

              <!-- Personalization Services -->
              <template v-if="selectedCategory === 'personalization'">
                <section
                  v-for="service in personalizationServices"
                  :key="service.id"
                  class="service-item"
                >
                  <div class="chekbox-container">
                    <label class="chekbox-label">
                      <div class="chekbox-label-wrapper">
                        <input type="checkbox" v-model="service.enabled" />
                        <div class="checkbox"></div>
                        <h3 class="desc">{{ service.name }}</h3>
                      </div>
                    </label>
                  </div>
                  <p class="desc description">{{ service.description }}</p>
                </section>
              </template>
            </div>

            <div class="cmp-buttons">
              <button
                type="button"
                class="btn btn-secondary"
                @click.prevent="selectedCategory = null"
              >
                {{ $t(`Less`) }}
              </button>
              <div class="cmp-buttons-actions">
                <button class="btn btn-primary" @click="saveCookiePreferences">
                  {{ $t(`Save`) }}
                </button>
                <button class="btn btn-secondary" @click="denyAllCookies">
                  {{ $t(`Deny all`) }}
                </button>
                <button class="btn btn-primary" @click="acceptAllCookies">
                  {{ $t(`Accept all`) }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import VueCookies from "vue-js-cookie";

export default {
  data() {
    return {
      cookiesAccepted: VueCookies.get("cookiesAccepted"),
      selectedCategory: null,
      essentialEnabled: true,
      performanceEnabled: false,
      personalizationEnabled: false,
      essentialServices: [
        {
          id: "cloudflare",
          name: "Cloudflare",
          description:
            "CloudFlare is an essential service for the availability of our website. The service increases the performance and security associated with page access. Cookies are also set for this purpose, which expire after 30 days. As part of CloudFlare's service, the company receives access to your IP address, which may also be processed outside the EU (e.g. in the USA).",
          enabled: true,
        },
        {
          id: "tradingview",
          name: "TradingView",
          description:
            "Essential for displaying cryptocurrency market charts and trading data. This service is necessary for core platform functionality and market data visualization.",
          enabled: true,
        },
      ],
      performanceServices: [
        {
          id: "error-tracking",
          name: "Error Tracking",
          description:
            "This service helps us identify and fix technical issues to ensure a smooth user experience. It collects anonymous data about any errors that occur during your visit.",
          enabled: false,
        },
        {
          id: "performance-monitoring",
          name: "Performance Monitoring",
          description:
            "Helps us optimize website loading and response times. This service collects anonymous performance metrics to improve our service quality.",
          enabled: false,
        },
      ],
      personalizationServices: [
        {
          id: "google-analytics",
          name: "Google Analytics (GA4)",
          description:
            "This is an analytical service used to understand how our website is used to improve user experience. Helps measure advertising activities effectiveness. Data collected includes Google Analytics client ID, IP address, and usage information.",
          enabled: false,
        },
        {
          id: "google-tag-manager",
          name: "Google Tag Manager",
          description:
            "The Google Tag Manager (GTM) helps manage various tracking and analytics tools. The service is responsible for the execution of certain codes and marketing tags.",
          enabled: false,
        },
        {
          id: "botpress",
          name: "Botpress",
          description:
            "Provides personalized chat assistance based on your interactions. This service helps us deliver better customer support through automated chat functionality.",
          enabled: false,
        },
      ],
    };
  },
  watch: {
    performanceEnabled(val) {
      this.performanceServices.forEach((service) => (service.enabled = val));
    },
    personalizationEnabled(val) {
      this.personalizationServices.forEach(
        (service) => (service.enabled = val)
      );
    },
  },
  methods: {
    showCategory(category) {
      this.selectedCategory = category;
    },
    acceptAllCookies() {
      this.performanceEnabled = true;
      this.personalizationEnabled = true;
      this.saveCookiePreferences();
    },
    denyAllCookies() {
      this.performanceEnabled = false;
      this.personalizationEnabled = false;
      this.saveCookiePreferences();
    },
    saveCookiePreferences() {
      const preferences = {
        essential: true,
        performance: this.performanceEnabled,
        personalization: this.personalizationEnabled,
        services: {
          performance: this.performanceServices.map((s) => ({
            id: s.id,
            enabled: s.enabled,
          })),
          personalization: this.personalizationServices.map((s) => ({
            id: s.id,
            enabled: s.enabled,
          })),
        },
      };

      VueCookies.set("cookiesAccepted", "true", 365);
      VueCookies.set("cookiePreferences", JSON.stringify(preferences), 365);

      this.cookiesAccepted = true;
      this.$emit("cookiesUpdated", preferences);
    },
  },
  created() {
    const savedPreferences = VueCookies.get("cookiePreferences");
    if (savedPreferences) {
      const preferences = JSON.parse(savedPreferences);
      this.performanceEnabled = preferences.performance;
      this.personalizationEnabled = preferences.personalization;

      if (preferences.services) {
        // Restore individual service settings
        preferences.services.performance.forEach((saved) => {
          const service = this.performanceServices.find(
            (s) => s.id === saved.id
          );
          if (service) service.enabled = saved.enabled;
        });
        preferences.services.personalization.forEach((saved) => {
          const service = this.personalizationServices.find(
            (s) => s.id === saved.id
          );
          if (service) service.enabled = saved.enabled;
        });
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.modal {
  &.cookie-modal {
    display: -webkit-flex;
    display: flex;
    -webkit-align-items: center;
    align-items: center;
    -webkit-justify-content: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
  }

  .wrapper {
    position: relative;
    max-width: 560px;
    width: 100%;
    margin: 24px;
    max-height: 90vh;
    background: #ffffff;
    border-radius: 16px;
    -webkit-border-radius: 16px;
    -webkit-box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.1);
    box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .cookie-content {
    width: 100%;
    height: 100%;
    max-height: 90vh;
    overflow-y: auto;
  }

  .cookie-view {
    padding: 40px;
  }

  .cmp-consentmodal-headline {
    font-family: "Manrope", sans-serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
    color: #282e38;
    text-align: center;
    margin-bottom: 24px;
  }

  .cmp-consentmodal-intro {
    font-family: "Open Sans", sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #7f8798;
    margin-bottom: 32px;
    text-align: center;
  }

  .cookie-categories {
    margin-bottom: 32px;
  }

  .cookie-category,
  .service-item {
    margin-bottom: 16px;
    padding: 24px;
    background: #f1f3f8;
    border-radius: 8px;
  }

  .chekbox-container {
    margin: 0;
  }

  .chekbox-label {
    margin-top: 0;
    cursor: pointer;
    width: 100%;
    -webkit-tap-highlight-color: transparent;
    display: -webkit-flex;
    display: flex;
  }

  .chekbox-label-wrapper {
    position: relative;
    padding-left: 40px;
    text-align: left;
    display: -webkit-flex;
    display: flex;
    -webkit-align-items: center;
    align-items: center;
    width: 100%;
  }

  .chekbox-label-wrapper::before {
    content: "";
    width: 28px;
    height: 28px;
    border-radius: 8px;
    -webkit-border-radius: 8px;
    background: #f1f3f8;
    position: absolute;
    left: 0;
    top: 50%;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
    cursor: pointer;
    -webkit-box-shadow: inset 0px 0px 12px #8a97b114;
    box-shadow: inset 0px 0px 12px #8a97b114;
    border: 1px solid #e5e7eb;
  }

  .checkbox {
    content: "";
    width: 16px;
    height: 12px;
    position: absolute;
    left: 6px;
    top: 50%;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
    background: url("../images/check.svg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0;
    visibility: hidden;
    -webkit-transition: 0.3s;
    transition: 0.3s;
    pointer-events: none;
  }

  input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    margin: 0;
    padding: 0;
  }

  input:checked + .checkbox {
    opacity: 1;
    visibility: visible;
  }

  .chekbox-label .desc {
    position: static;
    color: #7f8798;
    font-size: 16px;
    font-weight: 400;
    line-height: 28px;
    padding: 0;
  }

  .desc.description {
    font-family: "Open Sans", sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #7f8798;
    margin: 8px 0 12px 40px;
  }

  .details-btn {
    background: none;
    border: none;
    color: #0153fd;
    text-decoration: underline;
    cursor: pointer;
    margin-left: 40px;
    padding: 0;
    font-size: 14px;
  }

  .cmp-buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 32px;
    border-top: 1px solid #f1f3f8;
    padding-top: 24px;
  }

  .cmp-buttons-actions {
    display: flex;
    gap: 16px;
  }

  .btn {
    padding: 12px 24px;
    border-radius: 8px;
    font-family: "Manrope", sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    transition: opacity 0.2s;
    border: none;

    &.btn-secondary {
      background: #f1f3f8;
      color: #282e38;
    }
  }

  .btn-primary {
    background: #0153fd;
    color: #ffffff;
  }

  .link {
    color: #0153fd;
    text-decoration: underline;
    cursor: pointer;
  }
}

@media (max-width: 768px) {
  .wrapper {
    margin: 16px;
    max-height: 85vh;
  }

  .cookie-view {
    padding: 24px;
  }

  .cmp-buttons {
    flex-direction: column;
    gap: 16px;
  }

  .cmp-buttons-actions {
    width: 100%;
    justify-content: center;
  }

  .btn {
    padding: 12px 16px;
    font-size: 12px;
  }
}

@media not all and (min-resolution: 0.001dpcm) {
  @supports (-webkit-appearance: none) {
    .cookie-content {
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
    }

    .chekbox-label-wrapper::before {
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }
  }
}
</style>
